import { FunctionComponent, useEffect, useState } from 'react';
import { Card, SelectOption } from '@shopify/polaris';
import styled from 'styled-components';
import Checkbox from '@ui/checkbox';
import { Input, Select } from '@components/ui';
import { FlexboxContainer } from '@ui/common';
import { checkBoxRoundedCss } from '@styles/css';

interface RadioCardProps {
  additionalValue?: string;
  checked: boolean;
  disabled?: boolean;
  dropDownOptions?: SelectOption[];
  hasDropdown?: boolean;
  hasInput?: boolean;
  icon: React.ReactNode;
  id: string;
  onChangeAdditionalValue?(value: string): void;
  onClick(id: string, checked: boolean): void;
  placeholder?: string;
  text: string | React.ReactNode;
  subText?: string | React.ReactNode;
}

export const RadioCardContainer = styled.div<{
  checked: boolean;
}>`
  margin: 0 auto 2.3rem auto;
  cursor: pointer;
  width: 100%;

  .Polaris-Card {
    border: 2px solid rgba(255, 255, 255, 0);
    border: ${(props) =>
      props.checked
        ? `2px solid ${props.theme.colors.font.primary.hex}`
        : undefined};

    &:hover {
      border: 2px solid ${({ theme }) => theme.colors.font.secondary.hex};
    }
  }
`;

const AdditionalInputContainer = styled.div`
  padding: 0 2rem 2rem 2rem;
`;

const ContentContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 1.4rem;
`;

export const RadioCardIconContainer = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #f7f7f7;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const TextContainer = styled(FlexboxContainer)`
  flex-direction: column;
`;

const Text = styled.div`
  font-weight: 500;
  line-height: 20px;
  padding: 0 1.8rem;
`;

const SubText = styled.div`
  font-size: 12px;
  padding: 0 1.8rem;
`;

export const CheckboxContainer = styled.div`
  margin-left: auto;
  ${checkBoxRoundedCss}
`;

const RadioCard: FunctionComponent<RadioCardProps> = (props) => {
  const {
    additionalValue,
    checked,
    disabled,
    dropDownOptions,
    hasDropdown,
    hasInput,
    icon,
    id,
    onChangeAdditionalValue,
    onClick,
    placeholder,
    subText,
    text,
  } = props;

  const [value, setValue] = useState('');

  const handleOnClick = () => {
    onClick(id, !checked);
  };

  const handleDropdownOnChange = (selected: string, _id: string) => {
    setValue(selected);
  };

  const handleInputOnChange = (value: string) => {
    setValue(value);
  };

  useEffect(() => {
    onChangeAdditionalValue && onChangeAdditionalValue(value);
  }, [onChangeAdditionalValue, value]);

  return (
    <RadioCardContainer checked={checked}>
      <Card>
        <ContentContainer onClick={handleOnClick}>
          <RadioCardIconContainer>{icon}</RadioCardIconContainer>
          <TextContainer>
            <Text>{text}</Text>
            {subText && <SubText>{subText}</SubText>}
          </TextContainer>
          <CheckboxContainer>
            <Checkbox
              label=""
              labelHidden={true}
              checked={checked}
              disabled={disabled}
            />
          </CheckboxContainer>
        </ContentContainer>
        {checked && hasInput && (
          <AdditionalInputContainer>
            <Input
              label=""
              labelHidden
              onChange={handleInputOnChange}
              placeholder={placeholder}
              value={additionalValue}
            />
          </AdditionalInputContainer>
        )}
        {checked && hasDropdown && dropDownOptions && (
          <AdditionalInputContainer>
            <Select
              label=""
              labelHidden
              placeholder={placeholder}
              options={dropDownOptions}
              onChange={handleDropdownOnChange}
              value={additionalValue}
            />
          </AdditionalInputContainer>
        )}
      </Card>
    </RadioCardContainer>
  );
};

export default RadioCard;
